import { Injectable } from '@angular/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
    BOT_MESSAGE_ICON_DEFAULT,
    BOT_MESSAGE_ICON_LOADING,
    BotMessage,
    Message,
    UserMessage
} from 'src/app/chat-ai/models/chat-ai.models';
import { ChatAiDemoApiService } from 'src/app/chat-ai/services/api/demo/chat-ai-demo-api.service';
import { ChatAiStateService } from 'src/app/chat-ai/services/state/chat-ai-state.service';
import { ChatAiStorageService } from 'src/app/chat-ai/services/storage/chat-ai-storage.service';

import { cloneDeep } from 'lodash';

@Injectable()
export class ChatAiService {
    public readonly destroy$: Subject<void> = new Subject<void>();
    constructor(
        private apiService: ChatAiDemoApiService,
        private stateService: ChatAiStateService,
        private storageService: ChatAiStorageService
    ) {}

    sendMessage(message: string): void {
        if (!this.stateService.$canSendMessage()) {
            return;
        }

        const userMessage: Message = new UserMessage(message);
        const botMessage: Message = new BotMessage('', true);

        this.stateService.updateWithNewUserMessage(userMessage, botMessage);
        this.storageService.saveMessages(this.stateService.$messages());

        this.apiService
            .getBotResponse(userMessage.text)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: text => this.updateLastStreamingBotMessage({ text, isStreaming: true }),
                error: () => this.handleErrorBotResponse(),
                complete: () => {
                    this.updateLastStreamingBotMessage({ isStreaming: false });
                    this.storageService.saveMessages(this.stateService.$messages());
                }
            });
    }

    private updateLastStreamingBotMessage(botMsg: Partial<BotMessage>): void {
        const lastStreamingBotMsg: BotMessage = this.stateService.$messages().slice(-1)[0] as BotMessage;

        if (lastStreamingBotMsg?.isStreaming) {
            if (botMsg.text) {
                lastStreamingBotMsg.text = botMsg.text;
            }
            lastStreamingBotMsg.isStreaming = botMsg.isStreaming;
            lastStreamingBotMsg.iconName = botMsg.isStreaming ? BOT_MESSAGE_ICON_LOADING : BOT_MESSAGE_ICON_DEFAULT;

            this.stateService.updateWithBotStreamingMessage(lastStreamingBotMsg, !botMsg.text);
        }
    }

    private handleErrorBotResponse(): void {
        const messages: Message[] = cloneDeep(this.stateService.$messages());
        const lastStreamingBotMsg: BotMessage = messages[messages.length - 1] as BotMessage;
        if (!lastStreamingBotMsg.isStreaming) {
            return;
        }

        lastStreamingBotMsg.text += '\nFailed to get a full response. Please try again.';
        lastStreamingBotMsg.isStreaming = false;
        this.stateService.setMessages(messages);
    }
}
